/*
 * O cartão do evento no primeiro post:
 *
 *   ┌────┐ Nome do evento                                   (…)
 *   │OUT │ Público · Criado por [avatar] Fulano
 *   │ 22 │
 *   ⏱  qui, 22 out 10:00 → 11:00 (São Paulo)
 *   ↻  A cada duas semanas, quinta-feira
 *   👥 [avatares com ✓]
 *   📄 descrição
 *   [✓ Eu vou ▾] [☆ Interessado] [✕ Não vou]
 *
 * O bloco que o servidor desenhou (`.CanelaEvent`) continua no post: o cartão
 * entra dentro dele, e a descrição (já em HTML) é levada para a seção dela.
 * A presença vai pela API e o cartão novo volta no mesmo pedido.
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import Tooltip from 'flarum/common/components/Tooltip';
import classList from 'flarum/common/utils/classList';
import { downloadIcs, googleCalendarUrl } from './calendar';
import { eventRange, monthShort, readerZone, recurrenceLabel } from './format';
import { apiUrl, eventFromDocument, eventsSettings, personModel, postEvent } from './model';
import { openChannel } from '../../thirdparty/ramonChat/bridge';
import { t } from '../../utils/settings';
const InviteesModal = () => import(/* webpackChunkName: 'forum/features/events/InviteesModal', webpackMode: 'lazy-once' */ './InviteesModal');
export async function rsvp(post, event, status) {
  const payload = await app.request({
    method: 'POST',
    url: apiUrl(`/canela-events/${event.id}/rsvp`),
    body: {
      data: {
        type: 'canela-events',
        id: String(event.id),
        attributes: {
          status
        }
      }
    }
  });
  const card = payload ? eventFromDocument(payload) : null;
  if (card) post.pushAttributes({
    canelaEvent: card
  });
}
export default class EventCard extends Component {
  busy = null;
  oncreate(vnode) {
    super.oncreate(vnode);
    this.placeDescription();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.placeDescription();
  }

  /** A descrição do servidor entra na seção dela (uma vez; o nó fica lá). */
  placeDescription() {
    const slot = this.element?.querySelector('.CanelaEventCard-descriptionSlot');
    const description = this.attrs.description;
    if (slot && description && description.parentElement !== slot && description.textContent?.trim()) slot.appendChild(description);
  }
  view() {
    const post = this.attrs.post;
    const event = postEvent(post);
    if (!event) return null;
    const discussion = post.discussion();
    const title = event.name || discussion?.title() || '';
    const start = new Date(event.startsAt);
    const end = event.endsAt ? new Date(event.endsAt) : null;
    const hasDescription = !!this.attrs.description?.textContent?.trim();
    return m("div", {
      className: classList('CanelaEventCard', {
        'CanelaEventCard--ended': event.ended
      })
    }, m("header", {
      className: "CanelaEventCard-header"
    }, m("div", {
      className: "CanelaEventCard-date",
      "aria-hidden": "true"
    }, m("span", {
      className: "CanelaEventCard-month"
    }, monthShort(start)), m("span", {
      className: "CanelaEventCard-day"
    }, start.getDate())), m("div", {
      className: "CanelaEventCard-info"
    }, m("span", {
      className: "CanelaEventCard-name"
    }, title), m("div", {
      className: "CanelaEventCard-statusLine"
    }, m("span", {
      className: "CanelaEventCard-status"
    }, t(`events.status.${event.ended ? 'ended' : event.status}`)), event.creator && m('[', null, m("span", {
      className: "CanelaEventCard-separator"
    }, "\xB7"), m("span", {
      className: "CanelaEventCard-creator"
    }, m("span", {
      className: "CanelaEventCard-createdBy"
    }, t('events.created_by')), m(Link, {
      href: app.route.user(personModel(event.creator))
    }, m(Avatar, {
      user: personModel(event.creator)
    }), event.creator.displayName))))), m("div", {
      className: "CanelaEventCard-controls"
    }, this.moreMenu(event, title))), m("section", {
      className: "CanelaEventCard-section CanelaEventCard-dates"
    }, m(Icon, {
      name: "far fa-clock"
    }), m("p", null, m(Tooltip, {
      text: event.timezone ? `${event.timezone}` : t('events.local_time')
    }, m("span", {
      className: "CanelaEventCard-dateText"
    }, eventRange(start, end), readerZone() && m("span", {
      className: "CanelaEventCard-zone"
    }, " (", readerZone(), ")"))))), event.recurrence && m("section", {
      className: "CanelaEventCard-section CanelaEventCard-recurrence"
    }, m(Icon, {
      name: "fas fa-arrows-rotate"
    }), m("p", null, recurrenceLabel(event.recurrence, new Date(event.firstStartsAt)))), event.location && m("section", {
      className: "CanelaEventCard-section CanelaEventCard-location"
    }, m(Icon, {
      name: "fas fa-location-dot"
    }), m("p", null, event.location)), event.url && /^https?:\/\//i.test(event.url) && m("section", {
      className: "CanelaEventCard-section CanelaEventCard-url"
    }, m(Icon, {
      name: "fas fa-video"
    }), m("p", null, m("a", {
      href: event.url,
      target: "_blank",
      rel: "noopener noreferrer nofollow ugc"
    }, this.urlLabel(event.url)))), event.status !== 'standalone' && this.invitees(event), hasDescription && m("section", {
      className: "CanelaEventCard-section CanelaEventCard-description"
    }, m(Icon, {
      name: "far fa-file-lines"
    }), m("div", {
      className: "CanelaEventCard-descriptionSlot"
    })), event.chatChannelId && !this.chatDocked() && m("section", {
      className: "CanelaEventCard-section CanelaEventCard-chat"
    }, m(Icon, {
      name: "far fa-comments"
    }), m("p", null, m(Button, {
      className: "Button Button--link CanelaEventCard-chatLink",
      onclick: () => openChannel(event.chatChannelId)
    }, t('events.open_chat')))), this.actions(post, event));
  }

  /** O chat já está ao lado do tópico (o modo encaixado, no computador). */
  chatDocked() {
    return eventsSettings.dockedChat() && app.screen() !== 'phone' && document.body.classList.contains('CanelaEventChat--docked');
  }
  urlLabel(url) {
    try {
      const parsed = new URL(url);
      return /zoom\.us|youtube|youtu\.be|twitch|meet\.google|teams\.microsoft/.test(parsed.host) ? t('events.join_livestream', {
        host: parsed.host
      }) : url;
    } catch {
      return url;
    }
  }
  invitees(event) {
    if (!event.going && !event.interested) return null;
    const open = () => app.modal.show(InviteesModal, {
      event,
      title: event.name || this.attrs.post.discussion()?.title() || ''
    });
    return m("section", {
      className: "CanelaEventCard-section CanelaEventCard-invitees"
    }, m("button", {
      type: "button",
      className: "Button--ua-reset CanelaEventCard-inviteesIcon",
      onclick: open,
      "aria-label": t('events.see_invitees')
    }, m(Icon, {
      name: "fas fa-users"
    }), m("sub", null, event.going)), m("ul", {
      className: "CanelaEventCard-avatars"
    }, event.sample.map(person => m("li", {
      key: person.id,
      className: "CanelaEventCard-invitee"
    }, m("button", {
      type: "button",
      className: "Button--ua-reset",
      onclick: open,
      title: person.displayName
    }, m(Avatar, {
      user: personModel(person)
    }), m("span", {
      className: "CanelaEventCard-check",
      "aria-hidden": "true"
    }, m(Icon, {
      name: "fas fa-check"
    })))))), event.maxAttendees ? m("span", {
      className: "CanelaEventCard-capacity"
    }, t('events.capacity', {
      going: event.going,
      max: event.maxAttendees
    })) : null);
  }
  moreMenu(event, title) {
    const link = window.location.origin + app.route.discussion(this.attrs.post.discussion());
    return m(Dropdown, {
      className: "CanelaEventCard-more",
      buttonClassName: "Button Button--icon",
      menuClassName: "Dropdown-menu--right",
      icon: "fas fa-ellipsis",
      caretIcon: "",
      label: "",
      accessibleToggleLabel: t('events.more')
    }, m("a", {
      className: "Button",
      href: googleCalendarUrl(event, title, link),
      target: "_blank",
      rel: "noopener noreferrer"
    }, m(Icon, {
      name: "fab fa-google",
      className: "Button-icon"
    }), m("span", {
      className: "Button-label"
    }, t('events.add_google'))), m(Button, {
      icon: "far fa-calendar-plus",
      onclick: () => downloadIcs(event, title, link)
    }, t('events.add_ics')), event.status !== 'standalone' && m(Button, {
      icon: "fas fa-users",
      onclick: () => app.modal.show(InviteesModal, {
        event,
        title
      })
    }, t('events.see_invitees')), event.chatChannelId && m(Button, {
      icon: "far fa-comments",
      onclick: () => openChannel(event.chatChannelId)
    }, t('events.open_chat')));
  }
  actions(post, event) {
    if (event.status === 'standalone') return null;
    if (event.ended) {
      return m("section", {
        className: "CanelaEventCard-actions CanelaEventCard-actions--ended"
      }, m("span", null, t('events.ended_notice')));
    }
    if (!event.canRsvp) {
      return app.session.user ? null : m("section", {
        className: "CanelaEventCard-actions"
      }, m(Button, {
        className: "Button",
        icon: "fas fa-right-to-bracket",
        onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
      }, t('events.login_to_rsvp')));
    }
    const answer = status => {
      if (this.busy) return;
      this.busy = status || 'remove';
      rsvp(post, event, status).finally(() => {
        this.busy = null;
        m.redraw();
      });
    };
    const mine = event.myStatus;
    const fullForMe = event.full && mine !== 'going';
    return m("section", {
      className: classList('CanelaEventCard-actions', mine && `CanelaEventCard-actions--${mine}`)
    }, m("div", {
      className: classList('CanelaEventCard-going', {
        'CanelaEventCard-going--on': mine === 'going'
      })
    }, m(Button, {
      className: classList('Button', mine === 'going' ? 'Button--primary' : ''),
      icon: "fas fa-check",
      loading: this.busy === 'going',
      disabled: fullForMe,
      onclick: () => answer(mine === 'going' ? null : 'going')
    }, fullForMe ? t('events.full') : t('events.going')), mine === 'going' && m(Dropdown, {
      className: "CanelaEventCard-goingMenu",
      buttonClassName: "Button Button--primary Button--icon",
      menuClassName: "Dropdown-menu--right",
      icon: "fas fa-chevron-down",
      caretIcon: "",
      label: "",
      accessibleToggleLabel: t('events.more')
    }, m(Button, {
      icon: "fas fa-xmark",
      onclick: () => answer(null)
    }, t('events.remove_answer')))), m(Button, {
      className: classList('Button', {
        'Button--primary': mine === 'interested'
      }),
      icon: mine === 'interested' ? 'fas fa-star' : 'far fa-star',
      loading: this.busy === 'interested',
      onclick: () => answer(mine === 'interested' ? null : 'interested')
    }, t('events.interested')), m(Button, {
      className: classList('Button', {
        'Button--primary': mine === 'not_going'
      }),
      icon: "fas fa-xmark",
      loading: this.busy === 'not_going',
      onclick: () => answer(mine === 'not_going' ? null : 'not_going')
    }, t('events.not_going')));
  }
}
flarum.reg.add('ramon-canela', 'forum/features/events/EventCard', EventCard);flarum.reg.addChunkModule('forum/features/events/InviteesModal', './src/forum/features/events/InviteesModal.tsx', 'ramon-canela', 'forum/features/events/InviteesModal');